import { DiscussionContextType, DiscussionSummary } from "@cnx/common";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import DateFormat from "types/DateFormat";

const joinNonEmpty = (...parts: (string | null | undefined)[]): string =>
  parts.filter(Boolean).join(", ");

export const getContextDiscussionLabel = (
  discussion: DiscussionSummary,
  t: TFunction<"discussions">
): string => {
  switch (discussion.context) {
    case DiscussionContextType.RESOURCE_COMBINATION:
      return `${t("trip")}: ${joinNonEmpty(
        discussion.title,
        discussion.titleTrailers ? `${t("trailer")}: ${discussion.titleTrailers}` : null,
        discussion.titleTrucks ? `${t("truck")}: ${discussion.titleTrucks}` : null,
        discussion.titleDrivers ? `${t("driver")}: ${discussion.titleDrivers}` : null
      )}`;
    case DiscussionContextType.STOP:
      return `${t("stop")}: ${discussion.title ?? ""}`;
    case DiscussionContextType.SALES_ORDER_LEG:
      return `${t("shipment")}: ${joinNonEmpty(
        discussion.title,
        discussion.titleSender ? `${t("sender")}: ${discussion.titleSender}` : null,
        discussion.titleReceiver ? `${t("receiver")}: ${discussion.titleReceiver}` : null
      )}`;
    default:
      return discussion.title ?? "";
  }
};

export const getFilteredDiscussions = (
  discussions: DiscussionSummary[],
  search: string,
  t: TFunction<"discussions">
): DiscussionSummary[] => {
  if (!search) return discussions;

  const query = search.toLowerCase();

  return discussions.filter(discussion => {
    const label =
      discussion.discussionType === "private"
        ? discussion.participantName
        : getContextDiscussionLabel(discussion, t);
    return label?.toLowerCase().includes(query);
  });
};

export const getTimeAgoLabel = (
  timestamp: number,
  t: TFunction<"discussions">
): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday =
    now.getDate() === date.getDate() &&
    now.getMonth() === date.getMonth() &&
    now.getFullYear() === date.getFullYear();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    yesterday.getDate() === date.getDate() &&
    yesterday.getMonth() === date.getMonth() &&
    yesterday.getFullYear() === date.getFullYear();

  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfMessageDate = new Date(date);
  startOfMessageDate.setHours(0, 0, 0, 0);
  const oneWeekAgo = new Date(startOfToday);
  oneWeekAgo.setDate(startOfToday.getDate() - 7);
  const isOlderThanWeek = startOfMessageDate < oneWeekAgo;

  if (isOlderThanWeek) {
    return dayjs(date).format(DateFormat.ONLY_DATE);
  }

  if (isYesterday) {
    return (
      `${t("latestMessageTime.yesterday")}` +
      " " +
      dayjs(date).format(DateFormat.ONLY_TIME)
    );
  }

  if (isToday) {
    return dayjs(date).format(DateFormat.ONLY_TIME);
  }

  switch (date.getDay()) {
    case 1:
      return `${t("latestMessageTime.monday")}`;
    case 2:
      return `${t("latestMessageTime.tuesday")}`;
    case 3:
      return `${t("latestMessageTime.wednesday")}`;
    case 4:
      return `${t("latestMessageTime.thursday")}`;
    case 5:
      return `${t("latestMessageTime.friday")}`;
    case 6:
      return `${t("latestMessageTime.saturday")}`;
    case 0:
      return `${t("latestMessageTime.sunday")}`;
    default:
      return dayjs(date).format(DateFormat.ONLY_DATE);
  }
};
